import _ from "lodash";
import {
  LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH,
  LABEL_FORMAT_SELECTOR_LABEL_FORMAT_ID,
  LABEL_FORMAT_SELECTOR_LABEL_FORMAT_NAME
} from "../components/label-format-selector/constants";
import {toUpperCaseFirst} from "./formatters";
import {localizeI18nObj} from "./i18n";
import {
  FREQ_ANNUAL,
  FREQ_DAILY,
  FREQ_MONTHLY,
  FREQ_QUARTERLY,
  FREQ_SEMESTER,
  getFreqValueFromLayout,
  getFreqValueFromTimeValue,
  getMoment,
  getTimeValuesByFreq,
  SUPPORTED_FREQ_VALUES
} from "./timePeriodAndFreq";

export const FREQ_DIMENSION_KEY = "FREQ";

export const VARIATION_DIMENSION_KEY = "AUTO.VARIATION";
export const VARIATION_VALUE_VALUE_KEY = "V";
export const VARIATION_VALUE_TREND_KEY = "T";
export const VARIATION_VALUE_CYCLICAL_KEY = "C";

export const TIME_PERIOD_FORMAT_YEAR_KEY = "timePeriodFormatYear";
export const TIME_PERIOD_FORMAT_MONTH_KEY = "timePeriodFormatMonth";
export const TIME_PERIOD_FORMAT_DAY_KEY = "timePeriodFormatDay";
export const TIME_PERIOD_FORMAT_QUARTER_KEY = "timePeriodFormatQuarter";
export const TIME_PERIOD_FORMAT_SEMESTER_KEY = "timePeriodFormatSemester";

export const getVariationValueLabels = t => ({
  [VARIATION_VALUE_VALUE_KEY]: t ? t("customDimension.variation.options.value") : VARIATION_VALUE_VALUE_KEY,
  [VARIATION_VALUE_TREND_KEY]: t ? t("customDimension.variation.options.trend") : VARIATION_VALUE_TREND_KEY,
  [VARIATION_VALUE_CYCLICAL_KEY]: t ? t("customDimension.variation.options.cyclical") : VARIATION_VALUE_CYCLICAL_KEY
});

export const MARGINAL_DIMENSION_KEY = "MARGINAL";

export const MARGINAL_ATTRIBUTE_KEY = "MARGINAL";

export const NOTES_LABEL = "NOTES_LABEL";
export const NO_OFFICIAL_DATA_LABEL = "NO_OFFICIAL_DATA_LABEL";

const MAX_COL_COUNT = 10000;
const MAX_ROW_COUNT = 10000;

export const MAX_ALLOWED_CELLS = 1000000;

/** JsonStat Utils **/

export const getDimensionLabel = (jsonStat, datasetId, dimensionId, t) => {
  let dimensionLabel;

  if (dimensionId === VARIATION_DIMENSION_KEY) {
    return t ? t("customDimension.variation.label") : dimensionId;
  }

  if (dimensionId === MARGINAL_DIMENSION_KEY) {
    return t ? t("customDimension.indicator.label") : dimensionId;
  }

  if (jsonStat.dimension[dimensionId]) {
    dimensionLabel = jsonStat.dimension[dimensionId].label;
  } else if (datasetId) {
    dimensionLabel = jsonStat.extension.marginaldimensions[datasetId]?.[dimensionId]?.dimensionlabel || null;
  }

  return dimensionLabel ? toUpperCaseFirst(dimensionLabel) : dimensionId;
};

export const getFormattedDimensionLabel = (jsonStat, datasetId, dimensionId, format, t) => {
  return getDimensionLabel(jsonStat, datasetId, dimensionId, t);
};

export const getDimensionValueLabel = (jsonStat, datasetId, dimensionId, valueId, t) => {
  let valueLabel;

  if (dimensionId === VARIATION_DIMENSION_KEY) {
    return getVariationValueLabels(t)[valueId];
  }

  if (jsonStat.dimension[dimensionId]) {
    if (dimensionId !== MARGINAL_DIMENSION_KEY) {
      valueLabel = jsonStat.dimension[dimensionId].category.label[valueId];
    } else {
      valueLabel = jsonStat.extension.marginalvalues[valueId].label || valueId;
    }
  } else if (datasetId) {
    valueLabel = jsonStat.extension.marginaldimensions[datasetId]?.[dimensionId]?.values?.[valueId] || null;
  }

  return valueLabel ? toUpperCaseFirst(valueLabel) : valueId;
};

export const generateLocalizedTimePeriodFormatMapFromExtras = nodeExtras => {
  const ret = {};
  if (nodeExtras) {
    const language = sessionStorage.getItem("appLanguage");
    const languages = sessionStorage.getItem("appLanguages");
    const keys = [
      TIME_PERIOD_FORMAT_YEAR_KEY,
      TIME_PERIOD_FORMAT_MONTH_KEY,
      TIME_PERIOD_FORMAT_DAY_KEY,
      TIME_PERIOD_FORMAT_QUARTER_KEY,
      TIME_PERIOD_FORMAT_SEMESTER_KEY
    ];

    keys.forEach(key => {
      const config = nodeExtras[toUpperCaseFirst(key)] || {};
      ret[key] = localizeI18nObj(config, language, languages);
    });
  }

  return ret;
};

export const getLocalizedTimePeriodFormatMap = () => {
  const ret = {};

  const language = sessionStorage.getItem("appLanguage");
  const languages = sessionStorage.getItem("appLanguages");

  const keys = [
    TIME_PERIOD_FORMAT_YEAR_KEY,
    TIME_PERIOD_FORMAT_MONTH_KEY,
    TIME_PERIOD_FORMAT_DAY_KEY,
    TIME_PERIOD_FORMAT_QUARTER_KEY,
    TIME_PERIOD_FORMAT_SEMESTER_KEY
  ];

  keys.forEach(key => {
    const config = JSON.parse(sessionStorage.getItem(key) || "{}");
    ret[key] = localizeI18nObj(config, language, languages);
  });

  return ret;
};

const getFormattedTimeValueLabel = (value, dateInputFormat, dateOutputFormat) => {
  if (dateOutputFormat) {
    return getMoment(value, dateInputFormat).customFormat(dateOutputFormat);
  } else {
    return value;
  }
};

export const getFormattedDimensionValueLabel = (
  jsonStat,
  datasetId,
  dimensionId,
  valueId,
  format,
  t,
  localizedTimePeriodFormatMapExternal
) => {
  if (dimensionId === jsonStat.role?.time?.[0]) {
    const freq = getFreqValueFromTimeValue(valueId);

    const localizedTimePeriodFormatMap = localizedTimePeriodFormatMapExternal
      ? localizedTimePeriodFormatMapExternal
      : getLocalizedTimePeriodFormatMap();

    switch (freq) {
      case FREQ_ANNUAL: {
        return getFormattedTimeValueLabel(valueId, "YYYY", localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_YEAR_KEY]);
      }
      case FREQ_MONTHLY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-MM",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_MONTH_KEY]
        );
      }
      case FREQ_DAILY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-MM-DD",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_DAY_KEY]
        );
      }
      case FREQ_QUARTERLY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-Q[1-4]",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_QUARTER_KEY]
        );
      }
      case FREQ_SEMESTER: {
        return getFormattedTimeValueLabel(valueId, null, localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_SEMESTER_KEY]);
      }
    }
  }

  const valueLabel = getDimensionValueLabel(jsonStat, datasetId, dimensionId, valueId, t);

  switch (format) {
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_NAME:
      return valueLabel || valueId;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_ID:
      return valueId;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH:
      if (valueId && valueLabel && valueId !== valueLabel) {
        return `[${valueId}] ${valueLabel}`;
      } else {
        return valueLabel || valueId;
      }
    default:
      return valueLabel || valueId;
  }
};

export const getDataIdxFromCoordinatesArray = (dimensions, size) => {
  let dataIdx = 0;
  let multiplier = 1;

  // row-major order algorithm
  for (let i = 0; i < dimensions.length; i++) {
    multiplier *= i > 0 ? size[dimensions.length - i] : 1;
    dataIdx += multiplier * dimensions[dimensions.length - i - 1];
  }

  return dataIdx;
};

export const getCoordinatesArrayFromDataIdx = (dataIdx, size) => {
  const coordinates = new Array(size.length);

  // reverse row-major order algorithm
  let offset = dataIdx;
  for (let i = size.length - 1; i >= 0; i--) {
    const dimensionSize = size[i];
    coordinates[i] = dimensionSize > 0 ? offset % dimensionSize : 0;
    offset = dimensionSize > 0 ? Math.floor(offset / dimensionSize) : 0;
  }

  return coordinates;
};

export const getJsonStatTableSize = (jsonStat, layout) => {
  if (jsonStat) {
    let count = 1;

    if (!layout) {
      jsonStat.size.forEach(el => (count *= el));
    } else {
      let colCount = 1;
      (layout?.cols ?? []).forEach(col => (colCount *= jsonStat.size[jsonStat.id.indexOf(col)]));
      let rowCount = 1;
      (layout?.rows ?? []).forEach(row => (rowCount *= jsonStat.size[jsonStat.id.indexOf(row)]));
      (layout?.sections ?? []).forEach(section => (rowCount *= jsonStat.size[jsonStat.id.indexOf(section)]));
      count = colCount * rowCount;
    }

    return count;
  } else {
    return 0;
  }
};

export const getFilterTreeFromJsonStat = (filters, jsonStat) => {
  const tree = {};

  const filterDimIndices = filters.map(dim => jsonStat.id.indexOf(dim));
  const filterDimValues = filters.map(dim => jsonStat.dimension[dim].category.index);
  const size = jsonStat.size;

  Object.keys(jsonStat.value).forEach(key => {
    const coordinates = getCoordinatesArrayFromDataIdx(key, size);

    const treeRoute = filterDimIndices.map((dimIdx, i) => {
      const dimValueIdx = coordinates[dimIdx];
      return filterDimValues[i][dimValueIdx];
    });

    let currentLevel = tree;
    treeRoute.forEach(elem => {
      if (!currentLevel[elem]) {
        currentLevel[elem] = {};
      }
      currentLevel = currentLevel[elem];
    });
  });

  return tree;
};

export const isFilterValid = (filter, filterTree) => {
  let isValid = true;

  let iter = filterTree;
  filter.forEach(val => {
    if (isValid && iter[val]) {
      iter = iter[val];
    } else {
      isValid = false;
    }
  });

  return isValid;
};

export const getInitialFiltersValue = (jsonStat, layout, filterTree) => {
  const timeDim = jsonStat.role?.time?.[0];

  const primaryDim = layout.primaryDim?.[0] || null;
  const secondaryDim = layout.secondaryDim?.[0] || null;
  const filters = layout.filters || [];

  let selectedPrimaryDim = null;
  let selectedSecondaryDim = null;

  if (filters.length === 0) {
    return {};
  }

  let root = filterTree;
  if (primaryDim) {
    selectedPrimaryDim = Object.keys(root).find(
      primDimKey =>
        layout.primaryDimValues.includes(primDimKey) &&
        (!secondaryDim ||
          Object.keys(root[primDimKey]).find(secDimKey => layout.secondaryDimValues.includes(secDimKey)))
    );
    root = root[selectedPrimaryDim];
    if (secondaryDim) {
      selectedSecondaryDim = Object.keys(root).find(secDimKey => layout.secondaryDimValues.includes(secDimKey));
      root = root[selectedSecondaryDim];
    }
  }

  const filtersValue = {};

  let iter = root;
  layout.filters.forEach(dim => {
    const selectableValues = Object.keys(iter);
    const allValues = [...jsonStat.dimension[dim].category.index];
    if (dim === timeDim) {
      allValues.reverse();
    }
    const selectedVal = allValues.find(dimVal => selectableValues.includes(dimVal));
    filtersValue[dim] = selectedVal;
    iter = iter[selectedVal];
  });

  return filtersValue;
};

export const getInitialTableLayout = (
  jsonStat,
  territoryDim,
  timeDim,
  measureDim,
  asFlatTable = false,
  maxFlatTableColCount
) => {
  if (!jsonStat) {
    return null;
  }

  const maxCellCount = MAX_ROW_COUNT * MAX_COL_COUNT;

  const rows = [];
  const cols = [];
  const filters = [];

  const dimSize = {};
  jsonStat.id.forEach((dim, idx) => (dimSize[dim] = jsonStat.size[idx]));

  let remainingDimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  remainingDimensions.forEach(dim => {
    if (dimSize[dim] === 1) {
      filters.push(dim);
    }
  });
  remainingDimensions = remainingDimensions.filter(dim => !filters.includes(dim));

  if (!asFlatTable) {
    if (remainingDimensions.includes(FREQ_DIMENSION_KEY)) {
      cols.push(FREQ_DIMENSION_KEY);
    }
    if (timeDim && remainingDimensions.includes(timeDim)) {
      cols.push(timeDim);
    }
    remainingDimensions = remainingDimensions.filter(dim => !cols.includes(dim));

    let colCount = 1;
    cols.forEach(dim => (colCount *= dimSize[dim]));

    if (territoryDim && remainingDimensions.includes(territoryDim) && dimSize[territoryDim] * colCount < maxCellCount) {
      rows.push(territoryDim);
      filters.push(...remainingDimensions.filters(dim => dim !== territoryDim));
    } else {
      const row = remainingDimensions.find(dim => dimSize[dim] * colCount < maxCellCount);
      if (row) {
        rows.push(row);
        filters.push(...remainingDimensions.filters(dim => dim !== row));
      } else {
        filters.push(...remainingDimensions);
      }
    }
  } else {
    if (measureDim && remainingDimensions.includes(measureDim)) {
      const dimensionsWithoutMeasureDim = remainingDimensions.filter(dim => dim !== measureDim);
      if (
        maxFlatTableColCount === null ||
        maxFlatTableColCount === undefined ||
        dimSize[measureDim] < maxFlatTableColCount
      ) {
        cols.push(measureDim);
        rows.push(...dimensionsWithoutMeasureDim);
      } else {
        rows.push(...dimensionsWithoutMeasureDim, measureDim);
      }
    } else {
      rows.push(...remainingDimensions);
    }
  }

  return {
    rows: rows,
    cols: cols,
    sections: [],
    filters: filters
  };
};

export const getMultiViewerInitialTableLayout = (jsonStat, territoryDim, timeDim) => {
  if (!jsonStat) {
    return null;
  }

  const rows = [];
  let cols = [];

  let dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  rows.push(territoryDim);
  dimensions = dimensions.filter(dim => !rows.includes(dim));

  cols.push(timeDim);
  dimensions = dimensions.filter(dim => !cols.includes(dim));

  cols = cols.concat(dimensions);

  return {
    rows: rows,
    cols: cols,
    sections: [],
    filters: []
  };
};

export const getMultiViewerTableColCount = (jsonStat, layout) => {
  let colCount = 1;

  layout.cols.forEach(dim => {
    colCount *= jsonStat.size[jsonStat.id.indexOf(dim)];
  });

  return colCount;
};

export const getPointDataInitialTableLayout = (jsonStat, pointDim, territoryDim, timeDim, measureDim) => {
  if (!jsonStat) {
    return null;
  }

  let rows = [];
  const cols = [];

  let dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  rows.push(pointDim);
  if (territoryDim) {
    rows.push(territoryDim);
  }
  dimensions = dimensions.filter(dim => !rows.includes(dim));

  cols.push(timeDim);
  if (measureDim) {
    cols.push(measureDim);
  }
  dimensions = dimensions.filter(dim => !cols.includes(dim));

  rows = rows.concat(dimensions);

  return {
    rows: rows,
    cols: cols,
    sections: [],
    filters: []
  };
};

export const getInitialChartLayout = (jsonStat, _territoryDim) => {
  if (!jsonStat) {
    return null;
  }
  const primaryDim = [];
  let primaryDimValues = [];
  const secondaryDim = [];
  const secondaryDimValues = [];

  const dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  const timeDim = jsonStat.role?.time?.[0];

  if (dimensions.includes(timeDim)) {
    primaryDim.push(timeDim);
  } else {
    primaryDim.push(dimensions[0]);
  }
  primaryDimValues = jsonStat.dimension[primaryDim].category.index;

  const filters = dimensions.filter(dim => !primaryDim.includes(dim) && !secondaryDim.includes(dim));

  return {
    primaryDim: primaryDim,
    primaryDimValues: primaryDimValues,
    secondaryDim: secondaryDim,
    secondaryDimValues: secondaryDimValues,
    filters: filters
  };
};

export const getMultiViewerInitialChartLayout = (jsonStat, territoryDim, timeDim, _maxTerritoryValueCount) => {
  if (!jsonStat) {
    return null;
  }

  const primaryDim = [];
  const secondaryDim = [];
  const filters = [];

  const dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  primaryDim.push(territoryDim);

  secondaryDim.push(timeDim);

  dimensions.forEach(dim => {
    if (!primaryDim.includes(dim) && !secondaryDim.includes(dim)) {
      filters.push(dim);
    }
  });

  return {
    primaryDim: primaryDim,
    // removed usage of maxTerritoryValueCount for incompatibility with getFilterTreeFromJsonStat
    primaryDimValues: jsonStat.dimension[primaryDim].category.index, //.slice(0, maxTerritoryValueCount)
    secondaryDim: secondaryDim,
    secondaryDimValues: jsonStat.dimension[secondaryDim].category.index,
    filters: filters
  };
};

export const getPointDataInitialChartLayout = (jsonStat, geoDim, timeDim, measureDim, _maxTerritoryValueCount) => {
  if (!jsonStat) {
    return null;
  }
  const primaryDim = [];
  const secondaryDim = [];
  const filters = [];

  primaryDim.push(geoDim);

  secondaryDim.push(timeDim);

  if (measureDim) {
    filters.push(measureDim);
  }

  return {
    primaryDim: primaryDim,
    // removed usage of maxTerritoryValueCount for incompatibility with getFilterTreeFromJsonStat
    primaryDimValues: jsonStat.dimension[primaryDim].category.index, //.slice(0, maxTerritoryValueCount)
    secondaryDim: secondaryDim,
    secondaryDimValues: jsonStat.dimension[secondaryDim].category.index,
    filters: filters
  };
};

export const getInitialMapLayout = (jsonStat, territoryDim) => {
  if (!jsonStat) {
    return null;
  }

  const dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  if (!territoryDim || !jsonStat.id.includes(territoryDim)) {
    return null;
  }

  return {
    territoryDim: territoryDim,
    filters: dimensions.filter(dim => dim !== territoryDim)
  };
};

export const getPointDataInitialMapLayout = (jsonStat, geoDim, timeDim, measureDim) => {
  if (!jsonStat) {
    return null;
  }

  if (!geoDim || !jsonStat.id.includes(geoDim)) {
    return null;
  }

  const filters = [];

  filters.push(timeDim);
  if (measureDim) {
    filters.push(measureDim);
  }

  return {
    territoryDim: geoDim,
    filters: filters
  };
};

const getFilteredLayout = (initialLayout, jsonStat, isMultiViewerTable = false, isPointData = false) => {
  if (!initialLayout) {
    return null;
  }

  const layout = _.cloneDeep(initialLayout);
  const dimArraysKey = ["rows", "cols", "filters", "sections", "primaryDim", "secondaryDim", "territoryDim"];

  if (layout.territoryDim) {
    layout.territoryDim = [layout.territoryDim];
  }

  const dimensions = jsonStat.id.filter(dim => dim !== VARIATION_DIMENSION_KEY);

  // removing dimensions from layout not present in jsonStat
  for (const key in layout) {
    if (layout.hasOwnProperty(key)) {
      if (dimArraysKey.includes(key)) {
        layout[key] = (layout[key] || []).filter(dim => dimensions.includes(dim));
      }
    }
  }

  if (!isPointData) {
    // adding missing dimensions to layout
    dimensions.forEach(dim => {
      let found = false;
      for (const key in layout) {
        if (layout.hasOwnProperty(key)) {
          if (!found && dimArraysKey.includes(key)) {
            found = layout[key].includes(dim);
          }
        }
      }
      if (!found) {
        if (isMultiViewerTable) {
          layout.cols.push(dim);
        } else {
          layout.filters.push(dim);
          if (layout.filtersValue) {
            layout.filtersValue[dim] = jsonStat.dimension[dim].category.index[0];
          }
        }
      }
    });
  }

  // checking selected filtersValue
  if (layout.filtersValue) {
    for (const dim in layout.filtersValue) {
      if (layout.filtersValue.hasOwnProperty(dim)) {
        if (!layout.filters.includes(dim)) {
          layout.filtersValue[dim] = undefined;
        } else if (!jsonStat.dimension[dim].category.index.includes(layout.filtersValue[dim])) {
          layout.filtersValue[dim] = jsonStat.dimension[dim].category.index[0];
        }
      }
    }
  }

  // checking selected primaryDimValues
  if (layout.primaryDim) {
    if (layout.primaryDim.length === 1) {
      const primaryDim = layout?.primaryDim?.[0] || null;
      if (layout.primaryDimValues.length === 0) {
        layout.primaryDimValues = jsonStat.dimension[primaryDim].category.index;
      }
      layout.primaryDimValues = layout.primaryDimValues.filter(value =>
        jsonStat.dimension[primaryDim].category.index.includes(value)
      );
    } else {
      return getInitialChartLayout(jsonStat);
    }
  }

  // checking selected secondaryDimValues
  const secondaryDim = layout?.secondaryDim?.[0] || null;
  if (secondaryDim) {
    if (layout.secondaryDimValues.length === 0) {
      layout.secondaryDimValues = jsonStat.dimension[secondaryDim].category.index;
    }
    layout.secondaryDimValues = layout.secondaryDimValues.filter(value =>
      jsonStat.dimension[secondaryDim].category.index.includes(value)
    );
  }

  if (layout.territoryDim) {
    layout.territoryDim = layout.territoryDim[0];
  }

  return layout;
};

export const getFilteredTableLayout = (initialLayout, jsonStat, isMultiViewerTable, isPointData = false) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    rows: [],
    cols: [],
    sections: [],
    filters: [],
    ...initialLayout
  };

  return getFilteredLayout(layout, jsonStat, isMultiViewerTable, isPointData);
};

export const getFilteredMapLayout = (initialLayout, jsonStat, isPointData = false) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    territoryDim: "",
    filters: [],
    ...initialLayout
  };

  return getFilteredLayout(layout, jsonStat, false, isPointData);
};

export const getFilteredChartLayout = (initialLayout, jsonStat, isPointData = false) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    primaryDim: [],
    primaryDimValues: [],
    secondaryDim: [],
    secondaryDimValues: [],
    filters: [],
    ...initialLayout
  };

  return getFilteredLayout(layout, jsonStat, false, isPointData);
};

export const getUpdatedLayout = (dimension, value, layout, jsonStat, filterTree, timeDimension) => {
  const primaryDim = layout.primaryDim?.[0] || null;
  const secondaryDim = layout.secondaryDim?.[0] || null;

  const tmpLayout = _.cloneDeep(layout);
  if (primaryDim && dimension === primaryDim) {
    tmpLayout.primaryDimValues = value;
  } else if (secondaryDim && dimension === secondaryDim) {
    tmpLayout.secondaryDimValues = value;
  } else {
    tmpLayout.filtersValue[dimension] = value;
  }

  if (!filterTree) {
    return tmpLayout;
  }

  const timeDim = timeDimension || jsonStat?.role?.time?.[0];

  const filters = (tmpLayout?.primaryDim || []).concat(tmpLayout?.secondaryDim || []).concat(tmpLayout?.filters || []);
  let isLayoutValid = false;
  let iter = filterTree;
  if (primaryDim && !isLayoutValid) {
    (tmpLayout.primaryDimValues || []).forEach(primDimVal => {
      if (secondaryDim && !isLayoutValid) {
        (tmpLayout.secondaryDimValues || []).forEach(secDimVal => {
          if (filterTree[primDimVal][secDimVal] !== null && filterTree[primDimVal][secDimVal] !== undefined) {
            iter = filterTree[primDimVal][secDimVal];
            let isFilterValid = true;
            filters.slice(2).forEach(val => {
              if (
                isFilterValid &&
                iter[tmpLayout.filtersValue[val]] !== null &&
                iter[tmpLayout.filtersValue[val]] !== undefined
              ) {
                iter = iter[tmpLayout.filtersValue[val]];
              } else {
                isFilterValid = false;
              }
            });
            if (isFilterValid) {
              isLayoutValid = true;
            }
          }
        });
      } else {
        iter = filterTree[primDimVal];
        let isFilterValid = true;
        filters.slice(1).forEach(val => {
          if (
            isFilterValid &&
            iter[tmpLayout.filtersValue[val]] !== null &&
            iter[tmpLayout.filtersValue[val]] !== undefined
          ) {
            iter = iter[tmpLayout.filtersValue[val]];
          } else {
            isFilterValid = false;
          }
        });
        if (isFilterValid) {
          isLayoutValid = true;
        }
      }
    });
  } else {
    let isFilterValid = true;
    filters.forEach(val => {
      if (isFilterValid && iter[tmpLayout.filtersValue[val]]) {
        iter = iter[tmpLayout.filtersValue[val]];
      } else {
        isFilterValid = false;
      }
    });
    if (isFilterValid) {
      isLayoutValid = true;
    }
  }

  if (isLayoutValid) {
    return tmpLayout;
  } else {
    const newLayout = _.cloneDeep(layout);

    if (primaryDim && dimension === primaryDim) {
      newLayout.primaryDimValues = value;
      if (secondaryDim) {
        const allSecondaryDimValues = getDimensionFilterValues(secondaryDim, jsonStat, newLayout, filterTree);
        newLayout.secondaryDimValues = newLayout.secondaryDimValues.filter(val => allSecondaryDimValues.includes(val));
      }
      newLayout.filtersValue = getInitialFiltersValue(jsonStat, newLayout, filterTree);
    } else if (secondaryDim && dimension === secondaryDim) {
      newLayout.secondaryDimValues = value;
      newLayout.filtersValue = getInitialFiltersValue(jsonStat, newLayout, filterTree);
    } else {
      newLayout.filtersValue[dimension] = value;

      const filters = newLayout.filters || [];
      const selectedIdx = filters.indexOf(dimension);
      if (selectedIdx < filters.length - 1) {
        newLayout.filters.slice(selectedIdx + 1).forEach(dim => {
          const selectableValues = getDimensionFilterValues(dim, jsonStat, newLayout, filterTree);
          const allValues = [...jsonStat.dimension[dim].category.index];
          if (dim === timeDim) {
            allValues.reverse();
          }
          newLayout.filtersValue[dim] = allValues.find(dimVal => selectableValues.includes(dimVal));
        });
      }
    }

    return newLayout;
  }
};

export const getDimensionFilterValues = (dimension, jsonStat, layout, filterTree) => {
  if (!jsonStat) {
    return [];
  }

  const primaryDim = layout.primaryDim?.[0] || null;
  const secondaryDim = layout.secondaryDim?.[0] || null;
  const filters = (layout?.primaryDim || []).concat(layout?.secondaryDim || []).concat(layout?.filters || []);
  const dimensionIdx = filters.indexOf(dimension);
  const prevFilters = filters.slice(0, dimensionIdx);

  let data = {};
  if (dimensionIdx > 0) {
    let iter = filterTree;
    if (primaryDim) {
      (layout.primaryDimValues || []).forEach(primDimVal => {
        if (secondaryDim && dimension !== secondaryDim) {
          (layout.secondaryDimValues || []).forEach(secDimVal => {
            iter = filterTree[primDimVal][secDimVal];
            prevFilters.slice(2).forEach(val => {
              iter = (iter || {})[layout.filtersValue[val]];
            });
            data = {...data, ...iter};
          });
        } else {
          iter = filterTree[primDimVal];
          prevFilters.slice(1).forEach(val => {
            iter = (iter || {})[layout.filtersValue[val]];
          });
          data = {...data, ...iter};
        }
      });
    } else {
      prevFilters.forEach(val => {
        iter = iter[layout.filtersValue[val]] ?? {};
      });
      data = {...data, ...iter};
    }
  } else {
    data = filterTree;
  }

  return jsonStat.dimension[dimension].category.index.filter(val => data[val] !== undefined);
};

export const getDimensionValuesIndexesMap = jsonStat => {
  const indexesMap = {};

  jsonStat.id.forEach(dim => {
    indexesMap[dim] = {};
    jsonStat.dimension[dim].category.index.forEach((dimValue, idx) => (indexesMap[dim][dimValue] = idx));
  });

  return indexesMap;
};

export const getCompleteValueDims = (incompleteValueDims, dims, orderedDims, orderedDimValueTree) => {
  const ret = [...incompleteValueDims];

  try {
    let iter = orderedDimValueTree;
    orderedDims.forEach(dim => {
      const dimIdx = dims.indexOf(dim);
      if (!ret[dimIdx]) {
        ret[dimIdx] = Object.keys(iter)[0];
      }
      iter = iter[ret[dimIdx]];
    });
    return ret;
  } catch (e) {
    return null;
  }
};

export const getAttributeList = jsonStat => {
  const datasetId = jsonStat?.extension?.datasets?.[0];
  const observationAttributes = jsonStat?.extension?.attributes?.[datasetId]?.observation || [];
  const seriesAttributes = jsonStat?.extension?.attributes?.[datasetId]?.series || [];

  return [...observationAttributes, ...seriesAttributes].map(({id}) => id);
};

const getAttributeObject = (attributes, attributeIdx, attributeValueIdx, t) => {
  const attribute = attributes[attributeIdx];
  const attributeValue = attribute.values[attributeValueIdx];

  const id = attribute.id;
  const label = attribute.name !== NOTES_LABEL || !t ? attribute.name : t("customAttributes.notes.label");
  const valueId = attributeValue.id;
  const valueLabel =
    attributeValue.name !== NO_OFFICIAL_DATA_LABEL || !t
      ? attributeValue.name
      : t("customAttributes.notes.values.noOfficialData");

  return {
    id: id,
    label: label,
    valueId: valueId,
    valueLabel: valueLabel
  };
};

const getAttributeObjects = (attributeCoordinates, attributeObjList, t) => {
  const attributes = [];

  try {
    (attributeCoordinates || []).forEach((coordinate, idx) => {
      if (coordinate !== null) {
        const attribute = getAttributeObject(attributeObjList, idx, coordinate, t);
        attributes.push(attribute);
      }
    });

    return attributes.length > 0 ? attributes : null;
  } catch (error) {
    return -1;
  }
};

export const getDimensionAttributeMap = (jsonStat, t, excludedAttributes) => {
  if (!jsonStat) {
    return null;
  }

  const dimAttributeMap = {};

  const datasetIds = (jsonStat?.extension?.datasets || []).concat(MARGINAL_ATTRIBUTE_KEY);

  datasetIds.forEach(datasetId => {
    const datasetDimAttributeMap = {};

    const series = jsonStat?.extension?.attributes?.[datasetId]?.series || [];
    const seriesIndexes = jsonStat?.extension?.attributes?.[datasetId]?.index?.series || [];

    jsonStat.id.forEach(dim => (datasetDimAttributeMap[dim] = {}));
    getMarginalDimensions(jsonStat, datasetId).forEach(
      dim => (datasetDimAttributeMap[dim] = datasetDimAttributeMap[dim] || {})
    );

    if (series && series.length > 0 && seriesIndexes && seriesIndexes.length > 0) {
      seriesIndexes
        .filter(({coordinates}) => (coordinates || []).filter(c => c !== null).length === 1)
        .forEach(index => {
          const dimIdx = index.coordinates.findIndex(el => el !== null);
          const dimValueIdx = index.coordinates.find(el => el !== null);

          let dim, dimValue;
          if (datasetId === MARGINAL_DIMENSION_KEY || !jsonStat.id.includes(MARGINAL_DIMENSION_KEY)) {
            dim = jsonStat.id[dimIdx];
            dimValue = jsonStat.dimension[dim].category.index[dimValueIdx];
          } else {
            dim = jsonStat.extension.originalid[datasetId][dimIdx];
            dimValue = jsonStat.dimension[dim]
              ? jsonStat.dimension[dim].category.index[dimValueIdx]
              : jsonStat.extension.marginaldimensions[datasetId][dim].index[dimValueIdx];
          }

          const attributeObjects = getAttributeObjects(index.attributes, series, t);
          const filteredAttributeObjects =
            attributeObjects === null || attributeObjects === -1
              ? []
              : (attributeObjects ?? []).filter(({id}) => !(excludedAttributes ?? []).includes(id));

          if (filteredAttributeObjects.length > 0) {
            if (!datasetDimAttributeMap[dim][dimValue]) {
              datasetDimAttributeMap[dim][dimValue] = filteredAttributeObjects;
            }
          }
        });
    }

    dimAttributeMap[datasetId] = datasetDimAttributeMap;
  });

  return dimAttributeMap;
};

export const getObservationAttributeMap = jsonStat => {
  const obsAttributeMap = {};
  let hasError = false;

  (jsonStat?.extension?.datasets || []).forEach(datasetId => {
    const observations = jsonStat?.extension?.attributes?.[datasetId]?.observation || [];
    const observationIndexes = jsonStat?.extension?.attributes?.[datasetId]?.index?.observation || {};
    for (const key in observationIndexes) {
      if (observationIndexes.hasOwnProperty(key)) {
        const attributeObjects = getAttributeObjects(observationIndexes[key], observations);
        if (attributeObjects === -1) {
          hasError = true;
        } else {
          obsAttributeMap[key] = attributeObjects;
        }
      }
    }
  });

  return !hasError ? obsAttributeMap : null;
};

export const getDatasetAttributeMap = jsonStat => {
  const datasetAttributeMap = {};

  (jsonStat?.extension?.datasets || []).forEach(datasetId => {
    const datasetAttributes = [];

    const dataset = jsonStat?.extension?.attributes?.[datasetId]?.dataSet || [];
    const datasetIndexes = jsonStat?.extension?.attributes?.[datasetId]?.index?.dataSet || [];

    if (dataset && dataset.length > 0 && datasetIndexes && datasetIndexes.length > 0) {
      (datasetIndexes || []).forEach((el, idx) => {
        if (el !== null) {
          datasetAttributes.push(getAttributeObject(dataset, idx, el));
        }
      });
    }

    datasetAttributeMap[datasetId] = datasetAttributes;
  });

  return datasetAttributeMap;
};

export const getSeriesAttributeMap = (jsonStat, labelFormat, localizedTimePeriodFormatMapExternal) => {
  const seriesAttributeMap = {};

  const datasetIds = (jsonStat?.extension?.datasets || []).concat(MARGINAL_ATTRIBUTE_KEY);

  datasetIds.forEach(datasetId => {
    const seriesAttributes = [];

    const series = jsonStat?.extension?.attributes?.[datasetId]?.series || [];
    const seriesIndexes = jsonStat?.extension?.attributes?.[datasetId]?.index?.series || [];

    if (series && series.length > 0 && seriesIndexes && seriesIndexes.length > 0) {
      seriesIndexes
        .filter(({coordinates}) => (coordinates || []).filter(c => c !== null).length > 1)
        .forEach(({coordinates, attributes}) => {
          const dimsId = [];
          const dimsValueId = [];
          coordinates.forEach((dimValueIdx, dimIdx) => {
            if (dimValueIdx !== null) {
              let dim, dimValue;
              if (datasetId === MARGINAL_DIMENSION_KEY || !jsonStat.id.includes(MARGINAL_DIMENSION_KEY)) {
                dim = jsonStat.id[dimIdx];
                dimValue = jsonStat.dimension[dim].category.index[dimValueIdx];
              } else {
                dim = jsonStat.extension.originalid[datasetId][dimIdx];
                dimValue = jsonStat.dimension[dim]
                  ? jsonStat.dimension[dim].category.index[dimValueIdx]
                  : jsonStat.extension.marginaldimensions[datasetId][dim].index[dimValueIdx];
              }
              dimsId.push(dim);
              dimsValueId.push(dimValue);
            }
          });
          const dims = dimsId.map((dim, idx) => ({
            id: getFormattedDimensionLabel(jsonStat, null, dim, labelFormat),
            value: getFormattedDimensionValueLabel(
              jsonStat,
              null,
              dim,
              dimsValueId[idx],
              labelFormat,
              undefined,
              localizedTimePeriodFormatMapExternal
            )
          }));
          const attrs = [];
          attributes.forEach((attr, idx) => {
            if (attr !== null && attr !== undefined) {
              attrs.push(getAttributeObject(series, idx, attr));
            }
          });
          if (dims.length > 0 && attrs.length > 0) {
            seriesAttributes.push({
              dimensions: dims,
              attributes: attrs
            });
          }
        });
    }

    seriesAttributeMap[datasetId] = seriesAttributes;
  });

  return seriesAttributeMap;
};

export const getAttributeLabel = (attribute, _format) => {
  const id = attribute.id;
  const name = attribute.label;

  return name || id;
};

export const getAttributeValueLabel = (attribute, format) => {
  const id = attribute.valueId;
  const name = attribute.valueLabel;

  switch (format) {
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_NAME:
      return name || id;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_ID:
      return id;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH:
      return `[${id}] ${name}`;
    default:
      return name || id;
  }
};

export const getMarginalDimensions = (jsonStat, datasetId) =>
  datasetId && jsonStat?.extension?.marginaldimensions?.[datasetId]
    ? Object.keys(jsonStat.extension.marginaldimensions[datasetId]).sort(
        (a, b) =>
          jsonStat.extension.marginaldimensions[datasetId][a].position -
          jsonStat.extension.marginaldimensions[datasetId][b].position
      )
    : [];

export const getFiltersValueInfo = (
  filters,
  filtersValue,
  jsonStat,
  labelFormat,
  t,
  localizedTimePeriodFormatMapExternal
) => {
  let datasetId;
  if (jsonStat.id.includes(MARGINAL_DIMENSION_KEY)) {
    const marginal = jsonStat.extension.marginalvalues[filtersValue[MARGINAL_DIMENSION_KEY]];
    datasetId = marginal.label ? MARGINAL_ATTRIBUTE_KEY : marginal.datasetid;
  } else {
    datasetId = jsonStat.extension.datasets[0];
  }

  const values = [];
  filters.forEach(dim => {
    const filterValue = filtersValue[dim];
    if (dim !== MARGINAL_DIMENSION_KEY) {
      const datasetId = jsonStat?.extension?.datasets?.[0];
      values.push({
        dim: dim,
        dimLabel: getFormattedDimensionLabel(jsonStat, datasetId, dim, labelFormat, t),
        value: filterValue,
        valueLabel: getFormattedDimensionValueLabel(
          jsonStat,
          datasetId,
          dim,
          filterValue,
          labelFormat,
          t,
          localizedTimePeriodFormatMapExternal
        )
      });
    } else {
      const marginal = jsonStat.extension.marginalvalues[filterValue];
      if (marginal.label) {
        values.push({
          dim: dim,
          dimLabel: getFormattedDimensionLabel(jsonStat, null, dim, labelFormat, t),
          value: filterValue,
          valueLabel: marginal.label
        });
      } else {
        getMarginalDimensions(jsonStat, marginal.datasetid).forEach(dim => {
          values.push({
            dim: dim,
            dimLabel: getFormattedDimensionLabel(jsonStat, marginal.datasetid, dim, labelFormat, t),
            value: marginal.dimensionvalues[dim],
            valueLabel: getFormattedDimensionValueLabel(
              jsonStat,
              marginal.datasetid,
              dim,
              marginal.dimensionvalues[dim],
              labelFormat,
              t,
              localizedTimePeriodFormatMapExternal
            )
          });
        });
      }
    }
  });

  return {
    values,
    datasetId
  };
};

/** Codelists Utils (SVP) **/

export const getFormattedDimensionValueLabelFromCodelists = (
  dimId,
  valueId,
  codelistMaps,
  timeDim,
  format,
  localizedTimePeriodFormatMapExternal
) => {
  if (dimId === timeDim) {
    const freq = getFreqValueFromTimeValue(valueId);
    const localizedTimePeriodFormatMap = localizedTimePeriodFormatMapExternal ?? getLocalizedTimePeriodFormatMap();

    switch (freq) {
      case FREQ_ANNUAL: {
        return getFormattedTimeValueLabel(valueId, "YYYY", localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_YEAR_KEY]);
      }
      case FREQ_MONTHLY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-MM",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_MONTH_KEY]
        );
      }
      case FREQ_DAILY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-MM-DD",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_DAY_KEY]
        );
      }
      case FREQ_QUARTERLY: {
        return getFormattedTimeValueLabel(
          valueId,
          "YYYY-Q[1-4]",
          localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_QUARTER_KEY]
        );
      }
      case FREQ_SEMESTER: {
        return getFormattedTimeValueLabel(valueId, null, localizedTimePeriodFormatMap[TIME_PERIOD_FORMAT_SEMESTER_KEY]);
      }
    }
  }

  const valueLabel = codelistMaps?.[dimId]?.[valueId] ? toUpperCaseFirst(codelistMaps[dimId][valueId]) : null;

  switch (format) {
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_NAME:
      return valueLabel || valueId;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_ID:
      return valueId;
    case LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH:
      return valueLabel ? `[${valueId}] ${valueLabel}` : valueId;
    default:
      return valueLabel || valueId;
  }
};

export const getSVPInitialTableLayout = (
  dimensions,
  codelistLists,
  timeDim,
  freqDim,
  territoryDim,
  measureDim,
  maxCellCount = MAX_ALLOWED_CELLS,
  asFlatTable = false,
  maxFlatTableColCount
) => {
  if (!dimensions || !codelistLists) {
    return null;
  }

  const rows = [];
  const cols = [];
  const filters = [];

  const dimSize = {};
  dimensions.forEach(dim => (dimSize[dim] = codelistLists[dim].length));

  let remainingDimensions = [...dimensions];

  remainingDimensions.forEach(dim => {
    if (dimSize[dim] === 1) {
      filters.push(dim);
    }
  });
  remainingDimensions = remainingDimensions.filter(dim => !filters.includes(dim));

  if (!asFlatTable) {
    if (freqDim && remainingDimensions.includes(freqDim)) {
      cols.push(freqDim);
    }
    if (timeDim && remainingDimensions.includes(timeDim)) {
      cols.push(timeDim);
    }
    remainingDimensions = remainingDimensions.filter(dim => !cols.includes(dim));

    let colCount = 1;
    cols.forEach(dim => (colCount *= dimSize[dim]));

    if (territoryDim && remainingDimensions.includes(territoryDim) && dimSize[territoryDim] * colCount < maxCellCount) {
      rows.push(territoryDim);
      filters.push(...remainingDimensions.filters(dim => dim !== territoryDim));
    } else {
      const row = remainingDimensions.find(dim => dimSize[dim] * colCount < maxCellCount);
      if (row) {
        rows.push(row);
        filters.push(...remainingDimensions.filters(dim => dim !== row));
      } else {
        filters.push(...remainingDimensions);
      }
    }
  } else {
    if (measureDim && remainingDimensions.includes(measureDim)) {
      const dimensionsWithoutMeasureDim = remainingDimensions.filter(dim => dim !== measureDim);
      if (
        maxFlatTableColCount === null ||
        maxFlatTableColCount === undefined ||
        dimSize[measureDim] < maxFlatTableColCount
      ) {
        cols.push(measureDim);
        rows.push(...dimensionsWithoutMeasureDim);
      } else {
        rows.push(...dimensionsWithoutMeasureDim, measureDim);
      }
    } else {
      rows.push(...remainingDimensions);
    }
  }

  const filtersValue = {};
  filters.forEach(dim => {
    filtersValue[dim] =
      dim === freqDim
        ? SUPPORTED_FREQ_VALUES.find(freq => codelistLists[freqDim].includes(freq)) || codelistLists[freqDim][0]
        : dim === timeDim
          ? null
          : codelistLists[dim][0];
  });

  const layout = {
    rows: rows,
    cols: cols,
    sections: [],
    filters: filters,
    filtersValue: filtersValue
  };

  if (filters.includes(timeDim) && filtersValue[timeDim] === null) {
    const freqValue = getFreqValueFromLayout(layout, freqDim, codelistLists);
    const timeValues = getTimeValuesByFreq(codelistLists[timeDim], freqValue);
    layout.filtersValue[timeDim] = timeValues[timeValues.length - 1];
  }

  return layout;
};

export const getSVPInitialChartLayout = (dimensions, codelistLists, timeDim) => {
  if (!dimensions || !codelistLists) {
    return null;
  }

  const primaryDim = [];
  let primaryDimValues = [];
  const secondaryDim = [];
  const secondaryDimValues = [];

  if (dimensions.includes(timeDim)) {
    primaryDim.push(timeDim);
  } else {
    primaryDim.push(dimensions[0]);
  }
  primaryDimValues = codelistLists[primaryDim];

  const filters = dimensions.filter(dim => !primaryDim.includes(dim) && !secondaryDim.includes(dim));

  const filtersValue = {};
  filters.forEach(dim => {
    filtersValue[dim] = codelistLists[dim][0];
  });

  return {
    primaryDim: primaryDim,
    primaryDimValues: primaryDimValues,
    secondaryDim: secondaryDim,
    secondaryDimValues: secondaryDimValues,
    filters: filters,
    filtersValue: filtersValue
  };
};

export const getSVPInitialMapLayout = (dimensions, codelistLists, timeDim, freqDim, territoryDim) => {
  if (!dimensions || !codelistLists || !territoryDim || !codelistLists[territoryDim]) {
    return null;
  }

  const filters = dimensions.filter(dim => dim !== territoryDim);

  const filtersValue = {};
  filters.forEach(dim => {
    filtersValue[dim] =
      dim === freqDim
        ? SUPPORTED_FREQ_VALUES.find(freq => codelistLists[freqDim].includes(freq)) || codelistLists[freqDim][0]
        : dim === timeDim
          ? null
          : codelistLists[dim][0];
  });

  const layout = {
    territoryDim: territoryDim,
    filters: filters,
    filtersValue: filtersValue
  };

  if (filters.includes(timeDim) && filtersValue[timeDim] === null) {
    const freqValue = getFreqValueFromLayout(layout, freqDim, codelistLists);
    const timeValues = getTimeValuesByFreq(codelistLists[timeDim], freqValue);
    layout.filtersValue[timeDim] = timeValues[timeValues.length - 1];
  }

  return {
    territoryDim: territoryDim,
    filters: filters,
    filtersValue: filtersValue
  };
};

const getSVPFilteredLayout = (
  initialLayout,
  codelistLists,
  timeDim,
  freqDim,
  territoryDim,
  hasLastNPeriods = false
) => {
  if (!initialLayout) {
    return null;
  }

  const layout = _.cloneDeep(initialLayout);
  const dimArraysKey = ["rows", "cols", "filters", "sections", "primaryDim", "secondaryDim", "territoryDim"];

  const dimensions = Object.keys(codelistLists);

  if (layout.territoryDim && !dimensions.includes(layout.territoryDim)) {
    return getSVPInitialMapLayout(dimensions, codelistLists, timeDim, freqDim, territoryDim);
  }

  if (layout.territoryDim) {
    layout.territoryDim = [layout.territoryDim];
  }

  // removing dimensions from layout not present in the dataset
  for (const key in layout) {
    if (layout.hasOwnProperty(key)) {
      if (dimArraysKey.includes(key)) {
        layout[key] = (layout[key] || []).filter(dim => dimensions.includes(dim));
      }
    }
  }

  // adding missing dimensions to layout
  dimensions.forEach(dim => {
    let found = false;
    for (const key in layout) {
      if (layout.hasOwnProperty(key)) {
        if (!found && dimArraysKey.includes(key)) {
          found = layout[key].includes(dim);
        }
      }
    }
    if (!found) {
      layout.filters.push(dim);
    }
  });

  // checking selected filtersValue
  if (layout.filtersValue) {
    for (const dim in layout.filtersValue) {
      if (layout.filtersValue.hasOwnProperty(dim)) {
        if (!layout.filters.includes(dim) || !codelistLists[dim].includes(layout.filtersValue[dim])) {
          delete layout.filtersValue[dim];
        }
      }
    }
  }

  // checking selected primaryDimValues
  if (layout.primaryDim) {
    if (layout.primaryDim.length === 1) {
      const primaryDim = layout?.primaryDim?.[0] || null;
      if (layout.primaryDimValues.length === 0) {
        layout.primaryDimValues = codelistLists[primaryDim];
      } else {
        layout.primaryDimValues = layout.primaryDimValues.filter(value => codelistLists[primaryDim].includes(value));
      }
    } else {
      return getSVPInitialChartLayout(dimensions, codelistLists, timeDim);
    }
  }

  // checking selected secondaryDimValues
  const secondaryDim = layout?.secondaryDim?.[0] || null;
  if (secondaryDim) {
    if (layout.secondaryDimValues.length === 0) {
      layout.secondaryDimValues = codelistLists[secondaryDim];
    } else {
      layout.secondaryDimValues = layout.secondaryDimValues.filter(value =>
        codelistLists[secondaryDim].includes(value)
      );
    }
  }

  // adding missing value to filtersValue
  layout.filters.forEach(dim => {
    layout.filtersValue[dim] =
      dim === timeDim && hasLastNPeriods
        ? codelistLists[timeDim][codelistLists[timeDim].length - 1]
        : layout.filtersValue[dim]
          ? layout.filtersValue[dim]
          : dim === freqDim
            ? SUPPORTED_FREQ_VALUES.find(freq => codelistLists[freqDim].includes(freq)) || codelistLists[freqDim][0]
            : dim === timeDim
              ? null
              : codelistLists[dim][0];
  });
  if (layout.filters.includes(timeDim) && layout.filtersValue[timeDim] === null) {
    const freqValue = getFreqValueFromLayout(layout, freqDim, codelistLists);
    const timeValues = getTimeValuesByFreq(codelistLists[timeDim], freqValue);
    layout.filtersValue[timeDim] = timeValues[timeValues.length - 1];
  }

  if (layout.territoryDim) {
    layout.territoryDim = layout.territoryDim[0];
  }

  return layout;
};

export const getSVPFilteredTableLayout = (
  initialLayout,
  codelistLists,
  timeDim,
  freqDim,
  territoryDim,
  hasLastNPeriods
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    rows: [],
    cols: [],
    sections: [],
    filters: [],
    filtersValue: {},
    ...initialLayout
  };

  return getSVPFilteredLayout(layout, codelistLists, timeDim, freqDim, territoryDim, hasLastNPeriods);
};

export const getSVPFilteredMapLayout = (
  initialLayout,
  codelistLists,
  timeDim,
  freqDim,
  territoryDim,
  hasLastNPeriods
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    territoryDim: "",
    filters: [],
    filtersValue: {},
    ...initialLayout
  };

  return getSVPFilteredLayout(layout, codelistLists, timeDim, freqDim, territoryDim, hasLastNPeriods);
};

export const getSVPFilteredChartLayout = (
  initialLayout,
  codelistLists,
  timeDim,
  freqDim,
  territoryDim,
  hasLastNPeriods
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    primaryDim: [],
    primaryDimValues: [],
    secondaryDim: [],
    secondaryDimValues: [],
    filters: [],
    filtersValue: {},
    ...initialLayout
  };

  return getSVPFilteredLayout(layout, codelistLists, timeDim, freqDim, territoryDim, hasLastNPeriods);
};

export const getDimensionsInfo = (dimensions, codelistLists) => {
  const dimensionsInfo = {};
  if (dimensions && codelistLists) {
    dimensions.forEach(({id, label}) => {
      dimensionsInfo[id] = {
        id: id,
        label: label || id,
        size: codelistLists[id].length
      };
    });
  }

  return dimensionsInfo;
};

export const getDatasetSize = (
  dimensionsInfo,
  layout,
  ignorePrimaryDimSelectedValues,
  ignoreSecondaryDimSelectedValues
) => {
  if (!dimensionsInfo || !layout) {
    return -1;
  }

  let size = 1;

  const {filters = [], primaryDim = [], primaryDimValues = [], secondaryDim = [], secondaryDimValues = []} = layout;

  Object.keys(dimensionsInfo).forEach(dim => {
    if (filters.includes(dim)) {
      size *= 1;
    } else if (primaryDim.includes(dim) && primaryDimValues.length > 0 && !ignorePrimaryDimSelectedValues) {
      size *= primaryDimValues.length;
    } else if (secondaryDim.includes(dim) && secondaryDimValues.length > 0 && !ignoreSecondaryDimSelectedValues) {
      size *= secondaryDimValues.length;
    } else {
      size *= dimensionsInfo[dim].size;
    }
  });

  return size;
};

/** Combinations Utils (MVP) **/

export const getDimensionLabelFromCombinations = (dimId, dimensionLabels) => {
  const dimLabel = dimensionLabels?.[dimId] ? toUpperCaseFirst(dimensionLabels[dimId]) : null;
  return dimLabel || dimId;
};

export const getDimensionValueLabelFromCombinations = (dimId, valueId, dimensionMap) => {
  const valueLabel = dimensionMap?.[dimId]?.[valueId] ? toUpperCaseFirst(dimensionMap[dimId][valueId]) : null;
  return valueLabel || valueId;
};

export const getMVPInitialTableLayout = (dimensions, territoryDim, timeDim) => {
  const rows = [];
  let cols = [];

  rows.push(territoryDim);
  dimensions = dimensions.filter(dim => !rows.includes(dim));

  cols.push(timeDim);
  dimensions = dimensions.filter(dim => !cols.includes(dim));

  cols = cols.concat(dimensions);

  return {
    rows: rows,
    cols: cols,
    sections: [],
    filters: []
  };
};

export const getMVPInitialMapLayout = (dimensions, territoryDim, cols) => {
  return {
    territoryDim: territoryDim,
    filters: dimensions.filter(dim => dim !== territoryDim),
    filtersValue: cols[0]
  };
};

export const getMVPInitialChartLayout = (dimensions, territoryDim, timeDim, cols, rows, maxTerritoriesCount) => {
  const filters = dimensions.filter(dim => dim !== territoryDim && dim !== timeDim);
  return {
    primaryDim: [territoryDim],
    primaryDimValues: rows.slice(0, maxTerritoriesCount).map(row => row[territoryDim]),
    secondaryDim: [timeDim],
    secondaryDimValues: [...new Set(cols.map(col => col[timeDim]))],
    filters: filters,
    filtersValue: filters.reduce((acc, dim) => ({...acc, [dim]: cols[0][dim]}), {})
  };
};

const getMVPFilteredLayout = (initialLayout, dimensionIds, territoryDim, timeDim, cols, rows, maxTerritoriesCount) => {
  if (!initialLayout) {
    return null;
  }

  const layout = _.cloneDeep(initialLayout);
  const dimArraysKey = ["rows", "cols", "filters", "sections", "primaryDim", "secondaryDim", "territoryDim"];

  if (layout.territoryDim) {
    layout.territoryDim = [layout.territoryDim];
  }

  // removing dimensions from layout not present in dimensionIds
  for (const key in layout) {
    if (layout.hasOwnProperty(key)) {
      if (dimArraysKey.includes(key)) {
        layout[key] = (layout[key] || []).filter(dim => dimensionIds.includes(dim));
      }
    }
  }

  // adding missing dimensions to layout
  dimensionIds.forEach(dim => {
    let found = false;
    for (const key in layout) {
      if (layout.hasOwnProperty(key)) {
        if (!found && dimArraysKey.includes(key)) {
          found = layout[key].includes(dim);
        }
      }
    }
    if (!found) {
      layout.cols.push(dim);
    }
  });

  // checking selected filtersValue
  if (layout.filtersValue) {
    if (layout.filters.length > 0) {
      const filtersValues = cols.map(col => layout.filters.map(dim => col[dim]).join("+"));
      const currentFiltersValue = layout.filters.map(dim => layout.filtersValue[dim]).join("+");
      if (!filtersValues.includes(currentFiltersValue)) {
        layout.filtersValue = layout.filters.reduce((acc, dim) => ({...acc, [dim]: cols[0][dim]}), {});
      }
    }
  } else {
    layout.filtersValue = layout.filters.reduce((acc, dim) => ({...acc, [dim]: cols[0][dim]}), {});
  }

  if (layout.primaryDim && layout.secondaryDim) {
    if (layout.primaryDim.length === 1 && layout.secondaryDim.length === 1) {
      const primaryDim = layout.primaryDim[0];
      const secondaryDim = layout.secondaryDim[0];

      const timeValues = [...new Set(cols.map(col => col[timeDim]))];
      const territoryValues = rows.map(row => row[territoryDim]).slice(0, maxTerritoriesCount);

      layout.primaryDimValues = primaryDim === territoryDim ? territoryValues : timeValues;
      layout.secondaryDimValues = secondaryDim === territoryDim ? territoryValues : timeValues;
    } else {
      return getMVPInitialChartLayout(dimensionIds, territoryDim, timeDim, cols, rows, maxTerritoriesCount);
    }
  }

  if (layout.territoryDim) {
    layout.territoryDim = layout.territoryDim[0];
  }

  return layout;
};

export const getMVPFilteredTableLayout = (
  initialLayout,
  dimensionIds,
  territoryDim,
  timeDim,
  cols,
  rows,
  maxTerritoriesCount
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    rows: [],
    cols: [],
    sections: [],
    filters: [],
    ...initialLayout
  };

  return getMVPFilteredLayout(layout, dimensionIds, territoryDim, timeDim, cols, rows, maxTerritoriesCount);
};

export const getMVPFilteredMapLayout = (
  initialLayout,
  dimensionIds,
  territoryDim,
  timeDim,
  cols,
  rows,
  maxTerritoriesCount
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    territoryDim: "",
    filters: [],
    ...initialLayout
  };

  return getMVPFilteredLayout(layout, dimensionIds, territoryDim, timeDim, cols, rows, maxTerritoriesCount);
};

export const getMVPFilteredChartLayout = (
  initialLayout,
  dimensionIds,
  territoryDim,
  timeDim,
  cols,
  rows,
  maxTerritoriesCount
) => {
  if (!initialLayout) {
    return null;
  }
  const layout = {
    primaryDim: [],
    primaryDimValues: [],
    secondaryDim: [],
    secondaryDimValues: [],
    filters: [],
    ...initialLayout
  };

  return getMVPFilteredLayout(layout, dimensionIds, territoryDim, timeDim, cols, rows, maxTerritoriesCount);
};

export const getAttributeValueLabelFromCombinations = (attrId, valueId, attributesMap) => {
  let attrLabel = attrId;
  let valueLabel = valueId;
  const attr = attributesMap[attrId];
  if (attr) {
    attrLabel = attr.label || attrId;
    if (attr.values) {
      valueLabel = attr.values[valueId] || valueId;
    }
  }

  return {attrLabel, valueLabel};
};

/** Layout Utils **/

export const getDimensionsFromLayout = layout => {
  const dims = [];

  (layout?.rows ?? []).forEach(dim => dims.push(dim));
  (layout?.cols ?? []).forEach(dim => dims.push(dim));
  (layout?.sections ?? []).forEach(dim => dims.push(dim));
  (layout?.filters ?? []).forEach(dim => dims.push(dim));
  (layout?.primaryDim ?? []).forEach(dim => dims.push(dim));
  (layout?.secondaryDim ?? []).forEach(dim => dims.push(dim));
  if (layout?.territoryDim) {
    dims.push(layout.territoryDim);
  }

  return dims;
};
